import {ElementRef, Injectable} from '@angular/core';
import { SystemGlobals, SystemConstants } from './../globals';
import { MultipleSelectOption } from './common/multiple-select-control/multiple-select-control';
import { CookieService } from 'ngx-cookie-service';
import { StringTruth } from './common/common-types';
import { IToolPanelParams } from 'ag-grid-community';
import { DateTime } from 'luxon';

@Injectable()
export class SystemGlobalsService implements SystemGlobals {

  constants: SystemConstants;
  roles = Role;
  roleOptions: MultipleSelectOption[] = [];
  todaysDate: number;
  todaysDateString: string;

  helpOverviewHeaderRef: ElementRef;
  helpOverviewContentRef: any;

  showSystemBannerNotice: boolean = true;

  readonly ignoredRoles: number[] = [12, 14, 998, 999];
  readonly regularRoles: number[] = [2, 3, 4, 5, 6, 7, 8, 16, 21];

  private printableGrids: IToolPanelParams[] = [];

  constructor(private cookieService: CookieService) {
    this.constants = window['appGlobals'].constants;

    if (this.constants.authenticated) {

      // If user is required to change password, redirect them to change password
      if (this.constants.isPwReset) {
        window.location.href = '/users/index/' + this.constants._g_userID + '/1';
      }

      // If user is required to answer memos, then redirect them to memos
      let memoBypas = sessionStorage.getItem('memobypass') == "1";

      if (this.constants.userRole == Role.Student && this.constants.unreadMemos.length && !memoBypas && !window.location.pathname.includes('/main/communications/memos/read')) {
        window.location.href = '/main/communications/memos/read/' + this.constants.unreadMemos[0];
      }

      if (this.constants.translations && this.constants.translations.length) {

        let currentSelectedLang = (window['appGlobals'].constants.userLanguage === 'default') ?  window['appGlobals'].constants.language : window['appGlobals'].constants.userLanguage;
        this.constants.translations = this.constants.translations.reduce((acc: any, cur: any) => {
              let eng = cur['english']
              acc[eng] = cur[currentSelectedLang];
              return acc;
        }, {})

      }

      for (let role in Role) {
        if (parseInt(Role[role]) > 0 && !this.ignoredRoles.includes(parseInt(Role[role]))) this.roleOptions.push({
          id: Role[role],
          label: role.replace('_', ' '),
          value: Role[role],
          hide: false,
          selected: false,
          toggleSelected: () => {
          }
        });
      }

      if (cookieService.get(SystemCookies.showSystemBanner) == StringTruth.false || this.constants.systemBannerNotice.length == 0 || !this.constants.showSystemBannerNotice) this.showSystemBannerNotice = false;
    } else {
      // Redirect user to the sign in page
      let currentUrl = window.location.pathname;
      let bypassUrls = [
        '/main/help/overview',
        '/main/auth/reset-password',
        '/main/auth/otp',
        '/main/auth/new-login'
      ];

      let matchesBypass = bypassUrls.some(bypassUrl => currentUrl.includes(bypassUrl));

      if ((currentUrl != '/main/auth/signin') && !matchesBypass) {
        window.location.href = '/main/auth/signin';
      }
    }
  }

  registerPrintableGrid(grid: IToolPanelParams) {
    this.printableGrids.push(grid);
  }

  resetPrintableGrids() {
    this.printableGrids = [];
  }

  getPrintableGrids(): IToolPanelParams[] {
    return this.printableGrids;
  }

  get isDeveloper(): boolean {
    return this.constants._g_userRole == this.roles.Developer;
  }

  get isMasterAdmin(): boolean {
    return this.constants.hasMasterAdmin || this.constants._g_userRole == this.roles.Master_Administrator;
  }

  get companyName(): string {
    return this.constants.systemName == SystemName.Ascent ? 'aerostudies' : 'allantra';
  }

  //setting header position to fixed on help pages
  getTopHeaderRef(reference: ElementRef) {
    this.helpOverviewHeaderRef = reference;
  }

  getMainContentRef(reference: ElementRef) {
    this.helpOverviewContentRef = reference;
  }

  hideSystemBanner() {
    this.cookieService.set(SystemCookies.showSystemBanner, StringTruth.false);
    this.showSystemBannerNotice = false;
  }

  isFeatureEnabled(featureName: string): boolean {
    const features = this.constants.features;

    if (!features) {
      return false;
    }

    const normalizedFeatureName = featureName.toLowerCase();
    const matchingKey = Object.keys(features).find((key) => key.toLowerCase() === normalizedFeatureName);

    return matchingKey !== undefined && features[matchingKey] === true;
  }

  getUrlParamByName(name: string, url?: string) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');

    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    let results = regex.exec(url);

    if (!results) return null;
    if (!results[2]) return '';

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  updateQueryString(key, value, url) {
    if (!url) url = window.location.href;
    var re = new RegExp("([?&])" + key + "=.*?(&|#|$)(.*)", "gi"), hash;

    if (re.test(url)) {
      if (typeof value !== 'undefined' && value !== null) {
        return url.replace(re, '$1' + key + "=" + value + '$2$3');
      } else {
        hash = url.split('#');
        url = hash[0].replace(re, '$1$3').replace(/(&|\?)$/, '');
        if (typeof hash[1] !== 'undefined' && hash[1] !== null) {
          url += '#' + hash[1];
        }
        return url;
      }
    } else {
      if (typeof value !== 'undefined' && value !== null) {
        var separator = url.indexOf('?') !== -1 ? '&' : '?';
        hash = url.split('#');
        url = hash[0] + separator + key + '=' + value;

        if (typeof hash[1] !== 'undefined' && hash[1] !== null) {
          url += '#' + hash[1];
        }

        return url;
      } else {
        return url;
      }
    }
  }

  getTodaysJsDate(): number {
    if (this.todaysDate) return this.todaysDate;
    else {
      this.todaysDate = DateTime.local().toJSDate().getTime();
      return this.todaysDate;
    }
  }

  getTodaysJsDateString(): string {
    if (this.todaysDateString) return this.todaysDateString;
    else {
      this.todaysDateString = this.todaysDate.toString();
      return this.todaysDateString;
    }
  }

}

let systemGlobalsServiceStub: Partial<SystemGlobalsService> = {
  constants: JSON.parse('{"_g_networkID":106,"_g_networkEdition":"workplace","_g_networkName":"AeroSouth","_g_userID":23,"_g_userRole":5,"_g_userName":"Master  Admin","_g_systemName":"ASCENT","layoutIsImposter":false,"primaryColor":"#F58220","secondaryColor":"#C71D22","systemName":"ascent","layoutUserRoles":{"2":"Student","3":"Training Developer","4":"Safety Manager","5":"Registrar","6":"Manager","7":"Instructor","8":"Coach","16":"Communications","21":"Store Manager"},"lcEdition":"workplace","sideNavData":[{"menuItemID":10,"name":"Home","route":"/home/index","title":"Home","icon":"home","includefunction":false,"includefunction2":false,"alterfunction":"routeByRole","enabled":true,"modal":"","parentID":false,"children":[],"className":""},{"menuItemID":41,"name":"Records","route":"/records/index","title":"Records","icon":"th-list","includefunction":"byRole","includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":40,"children":[],"className":""},{"menuItemID":130,"name":"Content","route":"/modules/index","title":"Content","icon":"folder-open","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[{"menuItemID":131,"name":"Modules","route":"/modules/index","title":"Modules","icon":"tag","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","class":"","parentID":130,"children":[]},{"menuItemID":132,"name":"Curriculums","route":"/curriculum/index","title":"Curriculums","icon":"question-sign","includefunction":"byRole","includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","class":"","parentID":130,"children":[]},{"menuItemID":134,"name":"Certificates","route":"/certificate/index","title":"Certificates","icon":"certificate","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","class":"","parentID":130,"children":[]}],"className":""},{"menuItemID":60,"name":"People","route":"/users/students","title":"People","icon":"group","includefunction":"byLearningCenter","includefunction2":"checkManualDisable","alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[{"menuItemID":61,"name":"Students","route":"/users/students","title":"Students","icon":"group","includefunction":"byLearningCenter","includefunction2":"checkManualDisable","alterfunction":false,"enabled":true,"modal":"","class":"","parentID":60,"children":[]},{"menuItemID":65,"name":"Staff Reg","route":"/users/staff","title":"Staff","icon":"group","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","class":"","parentID":60,"children":[]}],"className":""},{"menuItemID":70,"name":"Reference Library","route":"/reference_library/index","title":"Reference Library","icon":"book","includefunction":"checkManualDisable","includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[],"className":""},{"menuItemID":160,"name":"Settings","route":"/settings/index","title":"Settings","icon":"cogs","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[],"className":""},{"menuItemID":300,"name":"Utilities","route":"/utilities","title":"Utilities","icon":"wrench","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[],"className":""},{"menuItemID":180,"name":"Tokens","route":"/tokens/index","title":"Tokens","icon":"minus-circle","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[],"className":""},{"menuItemID":110,"name":"Legal","route":"/license/index","title":"Legal","icon":"legal","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"","parentID":false,"children":[{"menuItemID":111,"name":"Software Licence","route":"","title":"Software Licence","icon":"save","includefunction":false,"includefunction2":false,"alterfunction":false,"enabled":true,"modal":"software-license-modal","class":"software-license-modal","parentID":110,"children":[]},{"menuItemID":112,"name":"Content Licence","route":"","title":"Content Licence","icon":"sitemap","includefunction":false,"includefunction2":false,"alterfunction":"enableWhenHasNetwork","enabled":true,"modal":"content-license-modal","class":"content-license-modal","parentID":110,"children":[]}],"className":"license-modal"}],"ROLE_STUDENT":2,"ROLE_TRAINING_DEVELOPER":3,"ROLE_SAFETY_MANAGER":4,"ROLE_REGISTRAR":5,"ROLE_MANAGER":6,"ROLE_INSTRUCTOR":7,"ROLE_COACH":8,"ROLE_MASTER_ADMINISTRATOR":12,"ROLE_VENDOR":14,"ROLE_COMMUNICATIONS":16,"ROLE_STORE_MANAGER":21,"MODULE_TOKEN":999,"DEMO_TOKEN":998,"roleOptions":[2,3,4,5,6,7,8,16,21],"softwareLicense":"","contentLicense":"","refererUrl":"http://aerodev4.aerostudies.com/app/utilities/testing"}')
};
export {systemGlobalsServiceStub};

export enum NetworkEdition {
  WORKPLACE = 'workplace',
  TRAINERS = 'trainers',
  HYBRID = 'hybrid',
}

export enum SystemName {
  Ascent = 'ascent',
  Avanti = 'avanti'
}

export enum SystemCookies {
  showSystemBanner = 'showSystemBanner',
  hideExpiredOlte = 'hideExpiredOlte',
  hideExpiredDocument = 'hideExpiredDocument',
}

export enum Role {
  Student = 2,
  Training_Developer = 3,
  Safety_Manager = 4,
  Registrar = 5,
  Manager = 6,
  Instructor = 7,
  Coach = 8,
  Master_Administrator = 12,
  Vendor = 14,
  Communications = 16,
  Store_Manager = 21,
  Module_Token = 999,
  Demo_Token = 998,
  Developer = 666,
  hybrid = 222
}

export class SelectOption {
  constructor(public title: string, public value: any) {
  }
}
